import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { FEATURE_PRICING, useFeatures } from '../auth/useFeatures';
import {
  PRICE_METHOD_CONTRACT,
  PRICE_METHOD_PERCENTAGE,
  PRICE_METHOD_STANDARD,
  fractionToPercent,
  getCustomerPricing,
  percentToFraction,
  setCustomerPricing,
} from './api';

interface Props {
  customerId: string;
}

const PRICING_KEY = 'customer-pricing';

/**
 * How a customer's lines get priced.
 *
 * **Its own card, behind pricing:read, and not part of the customer form.** A percentage discount
 * is margin information; customers:read — which every seeded WarehouseOperator and ReadOnly group
 * holds — would otherwise show it to anyone who can open a customer.
 *
 * **Contract pricing is built (M5.15).** The contracts themselves live in their own card below
 * this one; what this card decides is only WHICH of the three methods applies. An item the
 * contract does not mention still falls through to the item's volume tiers, exactly as legacy
 * does, and the notice says so rather than letting somebody assume the contract covers
 * everything.
 */
export function CustomerPricingCard({ customerId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();

  const features = useFeatures();

  // Two different questions, and both have to answer yes. The add-on says the tenant
  // bought Pricing; the permission says this user may see the structure. The server
  // enforces both independently -- this just avoids showing a screen that would 403.
  const canRead = features.has(FEATURE_PRICING) && has('pricing:read');
  // No add-on check here: canRead above already requires it and the component renders
  // nothing without it, so repeating it would be a condition no test can reach.
  const canWrite = has('pricing:write');

  const [method, setMethod] = useState<number>(PRICE_METHOD_STANDARD);
  const [percent, setPercent] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [PRICING_KEY, customerId] as const,
    queryFn: ({ signal }) => getCustomerPricing(customerId, signal),
    enabled: canRead,
  });

  useEffect(() => {
    if (!query.data) return;
    setMethod(query.data.priceMethod);
    setPercent(
      query.data.percentageDiscount === null ? '' : fractionToPercent(query.data.percentageDiscount),
    );
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      setCustomerPricing(customerId, {
        priceMethod: method,
        percentageDiscount:
          method === PRICE_METHOD_PERCENTAGE && percent !== ''
            ? percentToFraction(Number(percent))
            : null,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([PRICING_KEY, customerId], saved);
      setError(null);
      notifications.show({ color: 'teal', message: t('pricing.customer.saved') });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError && err.problem.detail
          ? err.problem.detail
          : t('pricing.customer.saveFailed'),
      );
    },
  });

  if (!canRead) return null;

  const options = [
    { value: String(PRICE_METHOD_STANDARD), label: t('pricing.customer.methodStandard') },
    { value: String(PRICE_METHOD_PERCENTAGE), label: t('pricing.customer.methodPercentage') },
    { value: String(PRICE_METHOD_CONTRACT), label: t('pricing.customer.methodContract') },
  ];

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Text fw={500}>{t('pricing.customer.title')}</Text>

        {query.isLoading && <Loader size="sm" color="invetrak" />}

        {query.isError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {t('pricing.customer.loadFailed')}
          </Alert>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        {query.isSuccess && (
          <>
            <Select
              label={t('pricing.customer.method')}
              description={t('pricing.customer.methodHelp')}
              data={options}
              value={String(method)}
              onChange={(v) => setMethod(Number(v ?? PRICE_METHOD_STANDARD))}
              disabled={!canWrite}
              allowDeselect={false}
            />

            {method === PRICE_METHOD_PERCENTAGE && (
              <NumberInput
                label={t('pricing.customer.percentage')}
                description={t('pricing.customer.percentageHelp')}
                value={percent}
                onChange={(v) => setPercent(v === '' ? '' : Number(v))}
                min={0}
                max={100}
                suffix="%"
                disabled={!canWrite}
              />
            )}

            {/* M5.15 built this. What is left to say is where the prices are set, and what
                happens to an item the contract does not mention — which is the fall-through
                people are most likely to be surprised by. */}
            {method === PRICE_METHOD_CONTRACT && (
              <Alert color="blue" variant="light">
                {t('pricing.customer.contractHow')}
              </Alert>
            )}

            {canWrite && (
              <Group justify="flex-end">
                <Button
                  size="xs"
                  color="invetrak"
                  loading={mutation.isPending}
                  onClick={() => mutation.mutate()}
                >
                  {t('pricing.customer.save')}
                </Button>
              </Group>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}
